import { forwardRef, useEffect, useRef } from "react";
import cx from "classnames";
import FocusLock from "react-focus-lock";
import { hideOthers } from "aria-hidden";

import Search from "./Search";
import Image from "./Image";
import SearchIcon from "assets/search-icon.svg";
import styles from "./ExpandedNav.module.scss";

const sections = [
  {
    text: "Politics",
    href: "https://www.theatlantic.com/politics/",
  },
  {
    text: "Ideas",
    href: "https://www.theatlantic.com/ideas/",
  },
  {
    text: "Photo",
    href: "https://www.theatlantic.com/photo/",
  },
  {
    text: "Science",
    href: "https://www.theatlantic.com/science/",
  },
  {
    text: "Culture",
    href: "https://www.theatlantic.com/culture/",
  },
  {
    text: "Podcasts",
    href: "https://www.theatlantic.com/podcasts/",
  },
  {
    text: "Health",
    href: "https://www.theatlantic.com/health/",
  },
  {
    text: "Education",
    href: "https://www.theatlantic.com/education/",
  },
  {
    text: "Planet",
    href: "https://www.theatlantic.com/planet/",
  },
  {
    text: "Technology",
    href: "https://www.theatlantic.com/technology/",
  },
  {
    text: "Family",
    href: "https://www.theatlantic.com/family/",
  },
  {
    text: "Projects",
    href: "https://www.theatlantic.com/projects/",
  },
  {
    text: "America In Person",
    href: "https://www.theatlantic.com/projects/america-in-person/",
  },
  {
    text: "Global",
    href: "https://www.theatlantic.com/international/",
  },
  {
    text: "Events",
    href: "https://www.theatlantic.com/events/",
  },
  {
    text: "Books",
    href: "https://www.theatlantic.com/books/",
  },
  {
    text: "Fiction",
    href: "https://www.theatlantic.com/category/fiction/",
  },
  {
    text: "Newsletters",
    href: "https://www.theatlantic.com/follow-the-atlantic/",
  },
];

interface MobileSearchProps {
  searchIsExpanded: boolean;
  closeSearch(): void;
}

const MobileSearch = ({ searchIsExpanded, closeSearch }: MobileSearchProps) => {
  const mobileSearchRef = useRef<HTMLDivElement>(null!);

  /**
   * Add `aria-hidden="true"` to everything else on the page when the modal is open.
   */
  useEffect(() => {
    let revealer = (): void => undefined;

    if (searchIsExpanded) {
      revealer = hideOthers(mobileSearchRef.current);
    } else {
      revealer();
    }

    return revealer;
  }, [searchIsExpanded]);

  return (
    // The FocusLock component traps focus inside children when it's rendered,
    // which is necessary for an accessible modal dialog
    <FocusLock
      returnFocus={true}
      autoFocus={false} // eslint-disable-line jsx-a11y/no-autofocus
      ref={mobileSearchRef}
      lockProps={{
        "aria-modal": true,
        "aria-labelledby": "mobile-search-label",
        role: "dialog",
      }}
    >
      <h2 id="mobile-search-label" className={styles.visuallyHidden}>
        Search The Atlantic
      </h2>
      <Search searchIsExpanded={searchIsExpanded} closeSearch={closeSearch} />
    </FocusLock>
  );
};

interface Props {
  navIsExpanded: boolean;
  searchIsExpanded: boolean;
  isMobile: boolean;
  openMobileSearch(): void;
  closeMobileSearch(): void;
  id: string;
}

const ExpandedNav = forwardRef<HTMLDivElement, Props>(
  (
    {
      navIsExpanded,
      searchIsExpanded,
      openMobileSearch,
      closeMobileSearch,
      isMobile,
      id,
    },
    ref
  ) => {
    const mobileSearchIsExpanded = searchIsExpanded && isMobile;

    return (
      <>
        <div
          hidden={!navIsExpanded}
          className={cx(styles.root, navIsExpanded && styles.expanded)}
          ref={ref}
          id={id}
        >
          <div className={styles.mobileHeader}>
            <button
              className={styles.searchButton}
              onClick={searchIsExpanded ? closeMobileSearch : openMobileSearch}
              aria-label="Search The Atlantic"
              data-action="open search"
            >
              <SearchIcon className={styles.searchIcon} />
            </button>
            <a
              className={styles.mostPopular}
              href="https://www.theatlantic.com/most-popular/"
              data-action="expanded"
            >
              Popular
            </a>
            <a
              className={styles.latest}
              href="https://www.theatlantic.com/latest/"
              data-action="expanded"
            >
              Latest
            </a>
          </div>
          <div className={styles.container}>
            <div className={styles.sections}>
              <h2 className={cx(styles.title, styles.sectionTitle)}>
                Sections
              </h2>
              <ul className={styles.sectionUl}>
                {sections.map((section) => {
                  return (
                    <li key={section.text} className={styles.sectionLi}>
                      <a
                        href={section.href}
                        data-action="expanded"
                        className={styles.sectionLink}
                      >
                        {section.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className={styles.crossword}>
              <h2 className={styles.title}>The Atlantic Crossword</h2>
              <div className={styles.crosswordContainer}>
                <div className={styles.crosswordBackground} />
                <Image
                  src="https://cdn.theatlantic.com/assets/media/files/nav-crossword.png"
                  className={styles.crosswordImg}
                  alt=""
                  width={266}
                  height={200}
                  lazy={true}
                />
                <a
                  href="https://www.theatlantic.com/free-daily-crossword-puzzle/"
                  className={styles.crosswordButton}
                  data-action="crossword"
                >
                  Play Crossword
                </a>
              </div>
            </div>
            <div className={styles.print}>
              <h2 className={cx(styles.title, styles.printTitle)}>
                The Print Edition
              </h2>
              <div className={styles.printContainer} data-action="magazine">
                <a
                  href="https://www.theatlantic.com/magazine/"
                  data-action="cover"
                  className={styles.printImgLink}
                >
                  <Image
                    src="https://www.theatlantic.com/magazine/images/current-issue.large.jpg"
                    alt=""
                    className={styles.printImg}
                    width={266}
                    height={200}
                    lazy={true}
                  />
                </a>
                <div className={styles.printLinks}>
                  <div className={styles.topPrintLinks}>
                    <a
                      href="https://www.theatlantic.com/magazine/"
                      data-action="latest"
                      className={styles.latestIssue}
                    >
                      Latest Issue
                    </a>
                    <a
                      href="https://www.theatlantic.com/magazine/backissues/"
                      data-action="past issues"
                      className={styles.pastIssues}
                    >
                      Past Issues
                    </a>
                  </div>
                  <hr className={styles.hr} />
                  <a
                    href="https://accounts.theatlantic.com/products/gift"
                    data-action="give a gift"
                    className={styles.giveAGift}
                  >
                    Give a Gift
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        {mobileSearchIsExpanded && (
          <MobileSearch
            searchIsExpanded={mobileSearchIsExpanded}
            closeSearch={closeMobileSearch}
          />
        )}
      </>
    );
  }
);

ExpandedNav.displayName = "ExpandedNav";

export default ExpandedNav;
